import { useCallback, useEffect, useState } from "react";
import { Search, Pencil, Trash2, StickyNote, History, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { QuickActions, Modal, Timeline, inputCls, F, SaveBtn, fmtINR, fmtDate } from "./CrmShared";

const blank = { name: "", company: "", phone: "", whatsapp: "", email: "", city: "", customer_id: "", note: "" };

function ContactModal({ open, onClose, contact, onSaved }) {
  const [f, setF] = useState(blank);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(contact ? { ...blank, ...contact, note: "" } : blank);
    api.get("/store-admin/customers").then((r) => setCustomers(r.data.customers || r.data || [])).catch(() => setCustomers([]));
  }, [open, contact]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickCustomer = (e) => {
    const c = customers.find((x) => (x.id || x._id) === e.target.value);
    if (c) setF({ ...f, customer_id: c.id || c._id, name: c.name || f.name, phone: c.phone || f.phone, email: c.email || f.email });
    else setF({ ...f, customer_id: "" });
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (contact?.id) await api.put(`/store-admin/crm/contacts/${contact.id}`, f);
      else await api.post("/store-admin/crm/contacts", f);
      toast.success(contact ? "Contact updated" : "Contact added");
      onSaved(); onClose();
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };
  return (
    <Modal open={open} onClose={onClose} title={contact ? "Edit Contact" : "Add Contact"} testid="contact-modal">
      <form onSubmit={submit} className="space-y-3">
        {customers.length > 0 && (
          <F label="Link existing store customer (optional)">
            <select data-testid="contact-link-customer" className={inputCls} value={f.customer_id} onChange={pickCustomer}>
              <option value="">— Select to autofill —</option>
              {customers.map((c) => <option key={c.id || c._id} value={c.id || c._id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</option>)}
            </select>
          </F>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Full name *"><input required data-testid="contact-name" className={inputCls} value={f.name} onChange={set("name")} /></F>
          <F label="Company"><input className={inputCls} value={f.company} onChange={set("company")} /></F>
          <F label="Phone"><input data-testid="contact-phone" className={inputCls} value={f.phone} onChange={set("phone")} /></F>
          <F label="WhatsApp"><input className={inputCls} value={f.whatsapp} onChange={set("whatsapp")} /></F>
          <F label="Email"><input type="email" className={inputCls} value={f.email} onChange={set("email")} /></F>
          <F label="City"><input className={inputCls} value={f.city} onChange={set("city")} /></F>
        </div>
        {!contact && <F label="Note"><textarea rows={2} className={`${inputCls} h-auto py-2`} value={f.note} onChange={set("note")} /></F>}
        <SaveBtn saving={saving} label={contact ? "Update Contact" : "Add Contact"} testid="contact-save" />
      </form>
    </Modal>
  );
}

export default function CrmContacts({ refreshKey }) {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");

  const load = useCallback(() => {
    api.get("/store-admin/crm/contacts", { params: { q } }).then((r) => setContacts(r.data.contacts)).catch((e) => toast.error(errMsg(e)));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);

  const del = async (c) => {
    if (!window.confirm(`Delete contact "${c.name}"?`)) return;
    try { await api.delete(`/store-admin/crm/contacts/${c.id}`); toast.success("Contact deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openHistory = async (c) => {
    try { const r = await api.get(`/store-admin/crm/contacts/${c.id}/history`); setHist(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/store-admin/crm/contacts/${noteFor.id}/notes`, { text: noteText }); toast.success("Note added"); setNoteFor(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const iconBtn = "h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted shrink-0";

  return (
    <div className="space-y-4" data-testid="crm-contacts">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="contacts-search" className={`${inputCls} pl-9`} placeholder="Search contacts…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button onClick={() => { setEdit(null); setModalOpen(true); }} data-testid="contacts-add"
          className="h-10 px-4 rounded-xl bg-foreground text-background text-xs font-extrabold flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add Contact</button>
      </div>

      {contacts.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="contacts-empty">No contacts yet.</div> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {contacts.map((c) => (
            <div key={c.id} className="rounded-2xl border border-border bg-background p-4 space-y-2" data-testid={`contact-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate">{c.name}{c.company ? <span className="font-medium text-muted-foreground"> · {c.company}</span> : ""}</p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{[c.phone, c.email, c.city].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                <QuickActions name={c.name} phone={c.phone} whatsapp={c.whatsapp} email={c.email} />
              </div>
              <div className="flex gap-1.5 pt-1">
                <button onClick={() => openHistory(c)} className={`${iconBtn} w-auto px-2.5 text-[11px] font-bold gap-1`} data-testid={`contact-history-${c.id}`}><History className="h-3.5 w-3.5" /> History</button>
                <button onClick={() => { setNoteFor(c); setNoteText(""); }} className={iconBtn} title="Add note" data-testid={`contact-note-${c.id}`}><StickyNote className="h-3.5 w-3.5" /></button>
                <button onClick={() => { setEdit(c); setModalOpen(true); }} className={iconBtn} title="Edit" data-testid={`contact-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(c)} className={`${iconBtn} hover:bg-red-50 hover:text-red-600`} title="Delete" data-testid={`contact-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} contact={edit} onSaved={load} />

      <Modal open={!!noteFor} onClose={() => setNoteFor(null)} title={`Note — ${noteFor?.name || ""}`} testid="contact-note-modal">
        <div className="space-y-3">
          <textarea rows={3} data-testid="contact-note-text" className={`${inputCls} h-auto py-2`} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Write a note…" />
          <button onClick={saveNote} data-testid="contact-note-save" className="w-full h-11 rounded-xl bg-foreground text-background font-bold text-sm">Add Note</button>
        </div>
      </Modal>

      <Modal open={!!hist} onClose={() => setHist(null)} title={hist?.contact?.name || "History"} testid="contact-history-modal">
        {hist && (
          <div className="space-y-5">
            <QuickActions name={hist.contact.name} phone={hist.contact.phone} whatsapp={hist.contact.whatsapp} email={hist.contact.email} />
            <div>
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2">Leads ({hist.leads.length})</p>
              {hist.leads.map((l) => <div key={l.id} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0"><span className="truncate">{l.requirement || l.source}</span><span className="font-bold shrink-0 ml-2">{l.stage}</span></div>)}
              {!hist.leads.length && <p className="text-sm text-muted-foreground">No leads.</p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2">Deals ({hist.deals.length})</p>
              {hist.deals.map((d) => <div key={d.id} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0"><span className="truncate">{d.name}</span><span className="font-bold shrink-0 ml-2">{fmtINR(d.value)} · {d.stage}</span></div>)}
              {!hist.deals.length && <p className="text-sm text-muted-foreground">No deals.</p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2 flex items-center gap-1"><ShoppingBag className="h-3 w-3" /> Store Orders ({hist.orders.length})</p>
              {hist.orders.map((o) => <div key={o.id} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0"><span className="truncate">{o.id?.slice(0, 8)}… · {fmtDate(o.created_at)}</span><span className="font-bold shrink-0 ml-2">{fmtINR(o.total || 0)}</span></div>)}
              {!hist.orders.length && <p className="text-sm text-muted-foreground">No store orders linked.</p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2">Activity</p>
              <Timeline activities={hist.activities} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
